/**
 * 運営の確認の画面（/staff/ads・2026-10-22 money/self-serve-ads-dash）。設計 §4 ④「運営の確認（最初の版は全部・1 件 1 分ほど）」・§6 の運営の画面。
 *
 * 口: 約束 §3（GET reviews・POST approve / reject）。鍵は運営の鍵（KUMO_STAFF_TOKEN）で、作り手のログインとは別。
 * WHY 鍵を画面に打たせ、メモリにだけ置くか: ダッシュボードに運営の権限の仕組みは無い（作り手のセッションでは通らない）。
 *   localStorage / sessionStorage に入れない = 共有の端末・ブラウザの拡張から読まれる所に残さない。タブを閉じれば消える。
 *   URL にも入れない（履歴・ログに残る）。作り手のナビには出さない（運営が URL を直に開く）。
 *
 * 1 件の見せ方（設計 §6）: バナー 2 つ・一文 → 行き先（slug・公開からの日数）・機械 / AI の判定と理由・広告主（id の頭・カードの入金の回数）。
 * 「Automatic check」（2026-10-22 ssa-founder-1022）: 決まりの確認（machine の行）と AI の中身のチェック（ai の行・説明 = note は運営だけ）の 2 行。
 *   以前は ai の行だけを読んでいて、AI の無いサーバーでは「—」だけになっていた（ssa-finish-result.md §5）。AI の行が無い = 「AI のチェックなし」と書く
 * 「だめ」は理由を 1 つ以上選ぶ（作り手の画面に理由が出る = AdDetailPage）。メモは作り手に見せない運営の記録（約束 §1 self_ad_reviews.note）。
 */
import { useState } from 'react';
import type { ApiError } from '../api';
import { errorText, useT } from '../i18n';
import { ago, Empty, ErrorState, PageHeader, Spinner } from '../components/ui';
import { AdBanner } from '../components/AdBanner';
import { TopBar } from './ProjectsPage';
import { isOff, staffApprove, staffReject, staffReviews } from '../selfAdsApi';
import { STAFF_REJECT_REASONS, aiVerdict, isKnownReason, lastReview, viewsFor, type StaffCampaign, type StaffReview } from '../selfAds';
import { money } from '../money';

export function StaffAdsReviewPage() {
  const { t } = useT();
  const [token, setToken] = useState('');
  const [key, setKey] = useState<string | null>(null);
  const [list, setList] = useState<StaffCampaign[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = async (k: string) => {
    setLoading(true);
    setError(null);
    try {
      setList((await staffReviews(k)).campaigns);
      setKey(k);
    } catch (e) {
      setError(e as ApiError);
      // 鍵の誤り（401 / 403）は鍵を捨てて打ち直させる
      if ((e as ApiError).status === 401 || (e as ApiError).status === 403) setKey(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrap">
      <TopBar />
      <main className="container" id="main">
        <PageHeader eyebrow={t('ssa.staff.eyebrow')} title={t('ssa.staff.title')} lead={list ? t('ssa.staff.count', { count: list.length }) : undefined}>
          {key && (
            <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={() => void load(key)}>
              {t('ssa.staff.reload')}
            </button>
          )}
        </PageHeader>

        {!key && (
          <form
            className="card ssa-staff-key"
            onSubmit={(e) => {
              e.preventDefault();
              if (token.trim()) void load(token.trim());
            }}
          >
            <label className="field">
              <span>{t('ssa.staff.key')}</span>
              <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false} />
              <small className="muted">{t('ssa.staff.keyHelp')}</small>
            </label>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={!token.trim() || loading}>
                {t('ssa.staff.open')}
              </button>
            </div>
          </form>
        )}

        {loading && !list && <Spinner />}
        {error && (isOff(error) ? <p className="alert alert-info">{t('ssa.off.body')}</p> : <ErrorState error={error} what={t('ssa.staff.title')} />)}

        {key && list && list.length === 0 && <Empty title={t('ssa.staff.none')} />}
        {key && list && list.length > 0 && (
          <ul className="ssa-review-list">
            {list.map((c) => (
              <ReviewItem key={c.id} c={c} token={key} onDone={() => setList((l) => (l ? l.filter((x) => x.id !== c.id) : l))} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function ReviewItem({ c, token, onDone }: { c: StaffCampaign; token: string; onDone: () => void }) {
  const { t } = useT();
  const [mode, setMode] = useState<'idle' | 'reject'>('idle');
  // AI の確認が挙げた理由を先に選んでおく（運営は外すだけで済む・見た目の確認 2026-10-22）。運営が選べない理由（missing_banner）は除く
  const [reasons, setReasons] = useState<string[]>(() => (aiVerdict(c.reviews)?.reasons ?? []).filter((r) => (STAFF_REJECT_REASONS as readonly string[]).includes(r)));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ai = aiVerdict(c.reviews);
  const machine = lastReview(c.reviews, 'machine');

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="card ssa-review">
      <div className="ad-banners">
        <AdBanner campaignId={c.id} size={320} assetId={c.banner320AssetId} tagline={c.tagline} name={c.targetName} staffToken={token} />
        <AdBanner campaignId={c.id} size={728} assetId={c.banner728AssetId} tagline={c.tagline} name={c.targetName} staffToken={token} />
      </div>
      <dl className="kv">
        <dt>{t('ssa.banner.line')}</dt>
        <dd>
          <strong>{c.tagline || c.targetName}</strong>
        </dd>
        <dt>{t('ssa.staff.goesTo')}</dt>
        <dd>
          <span className="mono">{c.targetSlug}</span> · {c.targetName} · {t('ssa.staff.age', { count: c.targetAgeDays })}
        </dd>
        <dt>{t('ssa.staff.ai')}</dt>
        <dd className="ssa-autocheck">
          <CheckLine label={t('ssa.staff.rules')} review={machine} />
          {ai ? <CheckLine label={t('ssa.staff.aiCheck')} review={ai} /> : <span className="small muted">{t('ssa.staff.aiOff')}</span>}
          {ai?.note && <span className="small ssa-autocheck-note">“{ai.note}”</span>}
        </dd>
        <dt>{t('ssa.staff.advertiser')}</dt>
        <dd>
          <span className="mono">{c.developerId.slice(0, 8)}</span> · {t('ssa.staff.topups', { count: c.advertiserTopupCount })}
        </dd>
        <dt>{t('ssa.staff.budget')}</dt>
        <dd>{t('ssa.budget.option', { amount: money(c.dailyCapCents, 'usd'), views: viewsFor(c.dailyCapCents, c.priceCentsPerMille) })}</dd>
        {c.submittedAt && (
          <>
            <dt>{t('ssa.staff.sent')}</dt>
            <dd>{ago(c.submittedAt)}</dd>
          </>
        )}
      </dl>

      {mode === 'idle' ? (
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setMode('reject')}>
            {t('ssa.staff.reject')}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run(() => staffApprove(token, c.id))}>
            {t('ssa.staff.approve')}
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reasons.length) void run(() => staffReject(token, c.id, reasons, note.trim() || undefined));
          }}
        >
          <fieldset className="ssa-reasons">
            <legend className="small">{t('ssa.staff.why')}</legend>
            {STAFF_REJECT_REASONS.map((r) => (
              <label key={r} className="check small">
                <input
                  type="checkbox"
                  checked={reasons.includes(r)}
                  onChange={(e) => setReasons((cur) => (e.target.checked ? [...cur, r] : cur.filter((x) => x !== r)))}
                />
                {t(`ssa.reason.${r}`)}
              </label>
            ))}
          </fieldset>
          <label className="field">
            <span>
              {t('ssa.staff.note')} <small className="muted">{t('ssa.staff.noteHelp')}</small>
            </span>
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setMode('idle')}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-danger" disabled={busy || reasons.length === 0}>
              {t('ssa.staff.rejectConfirm')}
            </button>
          </div>
        </form>
      )}
      {err && <p className="alert alert-error">{err}</p>}
    </li>
  );
}

/** 自動の確認の 1 行（決まり / AI）: 判定の印と理由 */
function CheckLine({ label, review }: { label: string; review: StaffReview | null }) {
  const { t } = useT();
  if (!review) return null;
  return (
    <span>
      <span className="small muted">{label}:</span>{' '}
      <span className={`pill ${review.verdict === 'ok' ? 'pill-ok' : review.verdict === 'ng' ? 'pill-danger' : 'pill-warn'}`}>{t(`ssa.staff.verdict.${review.verdict}`)}</span>{' '}
      {review.reasons.map((r) => (isKnownReason(r) ? t(`ssa.reason.${r}`) : r)).join(' · ')}
    </span>
  );
}
